import {
  Avatar,
  Box,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { LastContactedDate } from "contexts/QuickSend";
import moment from "moment";
import {
  BentoBrand,
  BentoContact,
  BentoContactResponseType,
  NewBentoContact,
} from "schemas/dashboard";

import { contactReplied } from "utils/influencer";
import { generateInitials } from "utils/string";

import styles from "./styles";

interface Props {
  brand: BentoBrand;
  selectedContact: BentoContact | NewBentoContact | null | undefined;
  expanded: boolean;
  loadingContacts: boolean;
  selectableContacts: BentoContact[];
  userAddedContacts: NewBentoContact[];
  isSubscribed: boolean;
  getLastContacted: (id: number) => LastContactedDate | undefined;
  onToggleExpand: () => void;
  onSelectContact: (contact: BentoContact | NewBentoContact) => void;
  onAddOwnContact: () => void;
  onRemoveBrand: () => void;
}

const isUserAddedContact = (
  contact: BentoContact | NewBentoContact | null | undefined,
): contact is NewBentoContact =>
  !!contact && (!("id" in contact) || !contact.id);

const Tag = ({
  bg,
  color,
  tooltip,
  children,
}: {
  bg: string;
  color: string;
  tooltip: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Tooltip title={tooltip} arrow placement="top">
    <Box
      component="span"
      sx={{
        backgroundColor: bg,
        color,
        px: 0.75,
        py: 0.25,
        borderRadius: "6px",
        fontSize: 10,
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        lineHeight: 1.2,
        cursor: "help",
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

interface ContactSignals {
  isExclusive: boolean;
  isVerified: boolean;
  hasResponded: boolean;
  lastContacted: LastContactedDate | null;
}

const getContactSignals = (
  contact: BentoContact | NewBentoContact | null | undefined,
  getLastContacted: Props["getLastContacted"],
): ContactSignals => {
  if (!contact || !("id" in contact)) {
    return {
      isExclusive: false,
      isVerified: false,
      hasResponded: false,
      lastContacted: null,
    };
  }
  const bento = contact as BentoContact;
  const isBounced = bento.bestResponseType === BentoContactResponseType.bounced;
  return {
    isExclusive: !!bento.sourcedFromUserInbox,
    isVerified: !!bento.lastSentToAt && !isBounced,
    hasResponded: contactReplied(bento),
    lastContacted: (bento.id ? getLastContacted(bento.id) : null) ?? null,
  };
};

interface VerificationTagsProps {
  signals: ContactSignals;
  isSubscribed: boolean;
}

const VerificationTags = ({ signals, isSubscribed }: VerificationTagsProps) => {
  if (!isSubscribed) return null;
  const { isExclusive, isVerified, hasResponded, lastContacted } = signals;
  const anyTag = isExclusive || isVerified || hasResponded || !!lastContacted;
  if (!anyTag) return null;

  const exclusiveTooltip = (
    <span>
      This is an <strong>exclusive contact</strong> (who is 2-3 times more
      likely to respond)
    </span>
  );

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.75,
      }}
    >
      {isExclusive && (
        <Tag bg="rgba(0,0,0,0.55)" color="#fff" tooltip={exclusiveTooltip}>
          <Box
            component="i"
            className="fa-solid fa-star"
            sx={{ fontSize: 8, color: "#FFD700" }}
          />
          Exclusive
        </Tag>
      )}
      {isVerified && (
        <Tag
          bg="#e0f2fe"
          color="#075985"
          tooltip="Bento has successfully delivered to this address before"
        >
          Verified
        </Tag>
      )}
      {isExclusive && (
        <Tag bg="#FFF8E1" color="#D97706" tooltip={exclusiveTooltip}>
          Engaged
        </Tag>
      )}
      {hasResponded && (
        <Tag
          bg="#E8F5E9"
          color="#388E3C"
          tooltip="This contact has replied to outreach in the past"
        >
          Has responded
        </Tag>
      )}
      {lastContacted && (
        <Tooltip
          title="The most recent date you emailed this contact"
          arrow
          placement="top"
        >
          <Typography
            sx={{
              fontSize: 10,
              color: "rgba(1,67,57,0.5)",
              cursor: "help",
            }}
          >
            Contacted {moment(lastContacted).format("MMM D, YYYY")}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

interface ExpandedContactSummaryProps {
  name?: string;
  title?: string;
  email: string;
}

const ExpandedContactSummary = ({
  name,
  title,
  email,
}: ExpandedContactSummaryProps) => (
  <Box sx={{ minWidth: 0 }}>
    {(name || title) && (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "baseline" },
          gap: { xs: 0, md: 1 },
          minWidth: 0,
        }}
      >
        {name && (
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 13,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flexShrink: 0,
              maxWidth: "100%",
            }}
          >
            {name}
          </Typography>
        )}
        {title && (
          <Typography
            sx={{
              fontSize: 12,
              color: "rgba(1,67,57,0.7)",
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              flexShrink: 1,
              maxWidth: "100%",
            }}
          >
            {title}
          </Typography>
        )}
      </Box>
    )}
    {email && (
      <Typography sx={{ fontSize: 11, color: "rgba(1,67,57,0.6)" }}>
        {email}
      </Typography>
    )}
  </Box>
);

const UserContactTag = () => (
  <Tag
    bg="#fef3c7"
    color="#92400e"
    tooltip="A contact you added — not from Bento's verified contacts"
  >
    Your contact
  </Tag>
);

const BrandContactRow = ({
  brand,
  selectedContact,
  expanded,
  loadingContacts,
  selectableContacts,
  userAddedContacts,
  isSubscribed,
  getLastContacted,
  onToggleExpand,
  onSelectContact,
  onAddOwnContact,
  onRemoveBrand,
}: Props) => {
  const selectedSignals = getContactSignals(selectedContact, getLastContacted);
  const selectedIsUserAdded = isUserAddedContact(selectedContact);

  const collapsedDisplayName = selectedContact
    ? selectedContact.name ||
      selectedContact.title ||
      `${brand.normalizedName} Team`
    : null;

  const actionButtons = (
    <>
      <Tooltip title="Remove brand from send">
        <IconButton
          size="small"
          onClick={onRemoveBrand}
          sx={{ p: { xs: 0.5, md: 0.75 } }}
        >
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={{ fontSize: 13, color: "rgba(1,67,57,0.6)" }}
          />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        onClick={onToggleExpand}
        sx={{ p: { xs: 0.5, md: 0.75 } }}
      >
        <Box
          component="i"
          className={`fa-regular fa-chevron-${expanded ? "up" : "down"}`}
          sx={{ fontSize: 12, color: "rgba(1,67,57,0.6)" }}
        />
      </IconButton>
    </>
  );

  return (
    <Box>
      <Box sx={[styles.row, expanded ? styles.expanded : {}]}>
        <Box sx={styles.brandHeader}>
          <Avatar
            src={brand.logoUrl || undefined}
            sx={{
              ...styles.brandAvatar,
              width: 28,
              height: 28,
              fontSize: 11,
              backgroundColor: brand.logoUrl ? undefined : "primary.light",
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            {generateInitials(brand.normalizedName)}
          </Avatar>

          <Box sx={styles.brandInfo}>
            <Typography sx={styles.brandName}>
              {brand.normalizedName}
            </Typography>
            {brand.instagramUsername && (
              <Typography sx={styles.brandHandle}>
                @{brand.instagramUsername}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={styles.divider} />

        {selectedContact ? (
          <Box sx={styles.contactInfo}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
                rowGap: 0.5,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {collapsedDisplayName}
              </Typography>
              {selectedIsUserAdded ? (
                <UserContactTag />
              ) : (
                <VerificationTags
                  signals={selectedSignals}
                  isSubscribed={isSubscribed}
                />
              )}
            </Box>
            {selectedContact.email && (
              <Typography
                sx={{
                  fontSize: 11,
                  color: "rgba(1,67,57,0.6)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedContact.email}
              </Typography>
            )}
          </Box>
        ) : loadingContacts ? (
          <Box sx={styles.contactInfo}>
            <Skeleton variant="text" width={140} sx={{ fontSize: 13 }} />
            <Skeleton variant="text" width={180} sx={{ fontSize: 11 }} />
          </Box>
        ) : (
          <Box sx={styles.contactInfo}>
            <Typography
              sx={{
                fontSize: 12,
                fontStyle: "italic",
                color: "rgba(1,67,57,0.45)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              No contact selected
            </Typography>
          </Box>
        )}

        <Box sx={styles.actions}>{actionButtons}</Box>
      </Box>

      {expanded && (
        <Box sx={styles.contactList}>
          {loadingContacts && (
            <Typography sx={{ fontSize: 12, color: "rgba(1,67,57,0.6)" }}>
              Loading contacts…
            </Typography>
          )}

          {!loadingContacts &&
            selectableContacts.map((contact) => {
              const isSelected =
                selectedContact &&
                "id" in selectedContact &&
                selectedContact.id === contact.id;
              const signals = getContactSignals(contact, getLastContacted);

              return (
                <Box
                  key={contact.id}
                  sx={[
                    styles.contactItem,
                    isSelected ? styles.contactItemSelected : {},
                  ]}
                  onClick={() => onSelectContact(contact)}
                >
                  <Box
                    component="i"
                    className={`fa-${
                      isSelected ? "solid" : "regular"
                    } fa-circle${isSelected ? "-dot" : ""}`}
                    sx={{
                      fontSize: 14,
                      mt: 0.25,
                      color: isSelected ? "primary.main" : "rgba(1,67,57,0.4)",
                    }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <ExpandedContactSummary
                      name={contact.name}
                      title={contact.title}
                      email={contact.email}
                    />
                    <Box sx={{ mt: 0.5 }}>
                      <VerificationTags
                        signals={signals}
                        isSubscribed={isSubscribed}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}

          {!loadingContacts &&
            selectableContacts.length === 0 &&
            userAddedContacts.length === 0 && (
              <Typography
                sx={{ fontSize: 12, color: "rgba(1,67,57,0.6)", py: 1 }}
              >
                No Bento contacts found for this brand.
              </Typography>
            )}

          {!loadingContacts &&
            userAddedContacts.map((contact) => {
              const isSelected =
                selectedIsUserAdded && selectedContact?.email === contact.email;

              return (
                <Box
                  key={`user-${contact.email}`}
                  sx={[
                    styles.contactItem,
                    isSelected ? styles.contactItemSelected : {},
                  ]}
                  onClick={() => onSelectContact(contact)}
                >
                  <Box
                    component="i"
                    className={`fa-${
                      isSelected ? "solid" : "regular"
                    } fa-circle${isSelected ? "-dot" : ""}`}
                    sx={{
                      fontSize: 14,
                      mt: 0.25,
                      color: isSelected ? "primary.main" : "rgba(1,67,57,0.4)",
                    }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <ExpandedContactSummary
                      name={contact.name}
                      title={contact.title}
                      email={contact.email}
                    />
                    <Box sx={{ mt: 0.5 }}>
                      <UserContactTag />
                    </Box>
                  </Box>
                </Box>
              );
            })}

          <Box sx={styles.addOwnContact} onClick={onAddOwnContact}>
            <Box component="i" className="fa-regular fa-plus" />
            <Typography sx={{ fontSize: 13 }}>Add your own contact</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BrandContactRow;
